import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'

import { fetchBankAccounts } from '@modules-deprecated/app/accounts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { isShineAccount } from '../../../utils/accountTypeCheckers'

const POLLING_INTERVAL_MS = 1500
const MAX_POLLING_DURATION_MS = 30000
const WAIT_PERIOD_MS = 4000

interface UseFetchShineBankAccountsProps {
  polling?: boolean
  pollingInterval?: number
  maxPollingDuration?: number
}

export const useFetchShineBankAccounts = ({
  polling = false,
  pollingInterval = POLLING_INTERVAL_MS,
  maxPollingDuration = MAX_POLLING_DURATION_MS,
}: UseFetchShineBankAccountsProps = {}) => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id || ''
  const [shouldPoll, setShouldPoll] = useState(false)
  const lastAccountCount = useRef(0)
  const timeoutRef = useRef<number | null>(null)
  const hasPolled = useRef(false)

  const { data, refetch } = useQuery(
    [QueryKeys.BookkeepingBankAccounts, organizationId],
    () => fetchBankAccounts(organizationId),
    {
      enabled: !!organizationId && shouldPoll,
      refetchInterval: shouldPoll ? pollingInterval : false,
      refetchIntervalInBackground: false,
    },
  )

  const shineBankAccounts = useMemo(() => {
    if (!data?.accounts) {
      return []
    }
    return data.accounts.filter((account) => isShineAccount(account))
  }, [data])

  const stopPolling = useCallback(() => {
    if (shouldPoll) {
      hasPolled.current = true
    }
    setShouldPoll(false)
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [shouldPoll])

  const startTimeout = useCallback(
    (duration: number) => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = window.setTimeout(() => {
        stopPolling()
      }, duration)
    },
    [stopPolling],
  )

  useEffect(() => {
    if (polling) {
      hasPolled.current = false
      setShouldPoll(true)
      startTimeout(maxPollingDuration)
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [polling, maxPollingDuration, stopPolling, startTimeout])

  useEffect(() => {
    const currentCount = shineBankAccounts.length

    // An account was found, start polling again with the wait timeout
    if (shouldPoll && currentCount > lastAccountCount.current && currentCount > 0) {
      lastAccountCount.current = currentCount
      startTimeout(WAIT_PERIOD_MS)
    }
  }, [shineBankAccounts.length, shouldPoll, startTimeout])

  return {
    shineBankAccounts,
    refetch,
    isPolling: polling && shouldPoll,
    hasPolled: hasPolled.current,
  }
}
